import { includesPeriod, periodsNotHeld } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import toStatementVerdict from 'modules/common/components/VerifiedStatementUpload/utils/statementVerdict';
import { ACTIVE_TASK_STATUSES, BaseTaskContext, Task, TaskStatus, TaskStatuses } from 'modules/Tasks/types';
import { AccountStatementCoverage, StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';
import { UploadedStatementStatus } from 'services/bookkeeping/queries/useStatementUploadStatuses';

/**
 * One child task of the `building_back_books` group: a single bank account and every month it owes.
 *
 * Mirrors `BulkBankStatementUploadContext` in `hyke/tasks/context/bookkeeping.py`. The server's
 * Pydantic model guarantees the two names are non-blank and `periods` holds at least one entry
 * with no duplicate `(year, month)` — a span that may cross a year boundary.
 */
export interface BulkBankStatementUploadContext extends BaseTaskContext {
    action_description: string;
    institution_name: string;
    /**
     * Logo only. Nullable, and one bank maps to many of these — never group or join on it.
     */
    plaid_institution_id: string | null;
    account_mask: string;
    /** May be an empty string. */
    account_type: string;
    /** Tight's bare numeric id, unprefixed — not comparable to coverage's `bka_`-prefixed string. */
    bank_account_id: number | null;
    /** The ask as it stood when the task was created. Goes stale; read coverage for what is owed. */
    periods: StatementPeriod[];
}

function isNonBlankString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
}

function isStatementPeriod(value: unknown): value is StatementPeriod {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Record<string, unknown>;
    return typeof candidate.year === 'number' && typeof candidate.month === 'number';
}

/**
 * Whether a task's context is a bulk statement-upload context. Custom group components must not
 * assume child-task shape — a group template could carry tasks of other types, or dev/seed data.
 *
 * The two names are checked non-blank rather than merely present: a blank one renders as an
 * unlabelled row rather than an obviously broken one.
 *
 * `institution_slug` used to be checked here too, as the key rows were to group under. Rows are
 * flat and keyed on the task id, and the join below is `bank_account_id` alone, so nothing ever
 * read the value -- only its presence, which kept a field alive that no longer had a reader.
 */
export function isBulkBankStatementUploadContext(context: unknown): context is BulkBankStatementUploadContext {
    if (typeof context !== 'object' || context === null) {
        return false;
    }
    const candidate = context as Record<string, unknown>;
    return (
        isNonBlankString(candidate.institution_name) &&
        isNonBlankString(candidate.account_mask) &&
        Array.isArray(candidate.periods) &&
        candidate.periods.length > 0 &&
        candidate.periods.every(isStatementPeriod)
    );
}

/**
 * A task context's account id in the form coverage reports, or null when it carries none.
 *
 * Coverage serializes the id through `V6BankAccountIdField` while the context stores it raw, so the
 * two never compare equal as written — one identity in two spellings, not two identities. The
 * context schema names the conversion: "The v6 API spells the same id `bka_<numeric>`". Normalising
 * is therefore a prefix, not a lookup.
 */
function v6AccountId(bankAccountId: number | null): string | null {
    return bankAccountId == null ? null : `bka_${bankAccountId}`;
}

/**
 * Whether this task names an account it can be matched by.
 *
 * A context without an id cannot be joined at all, and must not be joined by anything else — see
 * `toRows`. Exported so the panel can report the contract breach, which is the only reason a task
 * reaches this state.
 */
export function hasResolvableAccount(context: BulkBankStatementUploadContext): boolean {
    return v6AccountId(context.bank_account_id) !== null;
}

/**
 * What a row has to say about its account.
 *
 * `unknown` is not `complete`: an account with no required periods has an unresolved books start
 * date, which is a different fact from having supplied everything.
 */
export type BackBooksRowState = 'complete' | 'outstanding' | 'unknown';

export interface BackBooksRow {
    task: Task;
    account: AccountStatementCoverage;
    /**
     * The months *this task* is asking for: its context's periods, less any the account no
     * longer owes.
     *
     * The task is the ask, so it is what the member is shown and what closing it depends on. The
     * account's whole `required_periods` window is wider — it reaches back to the books start —
     * and using it here put months this task never asked about on the card, and every statement
     * credited across that window in the modal with a delete beside it.
     *
     * `context.periods` is maintained, not a snapshot: `_widen_open_ask` folds later months in on
     * every repeat ask. What it never does is shrink, so it is a union of everything ever asked
     * rather than what is still owed — hence the intersection here and `missing` below.
     *
     * Intersected because the two can legitimately disagree in one direction: the ask is built
     * from the coverage window (`outstanding_periods_by_account`: "The coverage read, unmodified,
     * is the ask"), so a context month outside it means the window itself moved — a corrected
     * books start. Such a month can no longer be credited by `upload-statuses` or corrected by
     * `reassign-periods`, so leaving it in would ask for something nothing can satisfy.
     */
    askedPeriods: StatementPeriod[];
    /**
     * The months this task is still waiting on: `askedPeriods` less what coverage holds.
     *
     * Coverage decides what is held, always — the context knows nothing about what has landed.
     */
    missing: StatementPeriod[];
    /** Decided here so the count and the completeness verdict cannot disagree on one row. */
    state: BackBooksRowState;
}

/**
 * The months this task asks for that the account still owes.
 *
 * Ordered by the account, not the context, so the range the member reads runs in coverage's own
 * order without a second sort.
 */
function askedPeriodsFor(
    context: BulkBankStatementUploadContext,
    account: AccountStatementCoverage
): StatementPeriod[] {
    return account.required_periods.filter((period) => includesPeriod(context.periods, period));
}

/**
 * What this task's row says, scoped to the months the task asked for.
 *
 * `unknown` stays a fact about the *account* — no required periods means the books start is not
 * resolved, so we do not yet know what to ask for. An empty `asked` with a resolved window is a
 * different thing: every month this task named has since stopped being owed, which is the task
 * being satisfied rather than undetermined, so it completes.
 */
function rowState(missing: StatementPeriod[], account: AccountStatementCoverage): BackBooksRowState {
    if (account.required_periods.length === 0) {
        return 'unknown';
    }
    return missing.length === 0 ? 'complete' : 'outstanding';
}

/**
 * Whether an ask is one the member can still answer.
 *
 * Tested for rather than inferred from `!== COMPLETED`. The panel asks for
 * `GROUP_PANEL_TASK_STATUSES`, so those three are the only statuses that arrive and the two
 * readings agree today — but they agree by the caller's query rather than by construction, and a
 * cancelled ask is not one anybody can answer. Inferred, it would suppress a completed row while
 * the only other task on the account was one nobody can act on.
 */
function isLiveAsk(task: Task): boolean {
    return (ACTIVE_TASK_STATUSES as readonly TaskStatus[]).includes(task.status);
}

/**
 * The accounts some still-open task is asking about.
 *
 * The panel fetches completed tasks as well as active ones, deliberately: a row the member has just
 * finished persists as its own confirmation rather than vanishing from under them. That holds while
 * an account has one task. It stops holding when an account has two.
 *
 * Two are reachable. The server's idempotency key is the account id, and its lookup excludes
 * terminal tasks — so a repeat ask returns the open task while one exists, and builds a *new* one
 * once the first is cleared. An account whose months came due again therefore carries a completed
 * ask and a live one at the same time.
 *
 * Both rows render from the same coverage account, and this row deliberately never renders its own
 * task's status, so the pair is indistinguishable: the same bank, the same mask, the same
 * outstanding count, twice. The completed one is the superseded copy — it is answering an ask that
 * a later task has already taken over — so it is the one that goes.
 *
 * Supersession rather than staleness, which is where this parts company with
 * `MonthlyReimbursementGroup`'s `isActiveOrRecentlyCompleted`. That group drops completed rows by
 * age, to stop a permanent group piling up. Age is the wrong test here: these two tasks can be
 * minutes apart, and a lone completed ask should still show its tick however old it is. What makes
 * this one redundant is the live task beside it, not its age.
 */
function accountIdsWithOpenAsk(tasks: Task[]): Set<string> {
    return new Set(
        tasks.flatMap((task) => {
            if (!isLiveAsk(task) || !isBulkBankStatementUploadContext(task.context)) {
                return [];
            }
            const id = v6AccountId(task.context.bank_account_id);
            return id === null ? [] : [id];
        })
    );
}

/**
 * Sort key for two asks on one account: newest first, ties broken on the id.
 *
 * Parsed rather than compared as strings. Two stamps this only ever sees together are formatted
 * alike, so a lexicographic compare would work — right up to the day one of them carries an offset
 * instead of a `Z`, which is a silent reordering rather than an error. The id breaks a genuine tie
 * so the row that renders cannot flip between two asks stamped the same second.
 */
function askOrder(task: Task): number {
    const stamped = Date.parse(task.created_at);
    return Number.isNaN(stamped) ? 0 : stamped;
}

function isNewerAsk(candidate: Task, incumbent: Task): boolean {
    const byTime = askOrder(candidate) - askOrder(incumbent);
    return byTime === 0 ? candidate.id > incumbent.id : byTime > 0;
}

/**
 * The completed asks worth rendering: the newest one per account, and no more.
 *
 * `accountIdsWithOpenAsk` drops a superseded completed ask only while the live ask beside it is
 * still live — and that is a state the panel itself ends. It completes the live task on the very
 * coverage read that satisfies it, at which point the account holds two *completed* asks, nothing
 * suppresses either, and the duplicate row the suppression exists to prevent renders anyway,
 * seconds after it was prevented.
 *
 * So liveness decides which copy wins and this decides how many there are. Both rules are needed:
 * dropping every completed ask but the newest would still leave the pair rendering while one of
 * them was live and outstanding, and suppressing by liveness alone lasts only as long as the
 * liveness does.
 *
 * Keyed on the coverage account rather than the task context, matching THE JOIN below: two asks
 * are duplicates when they are answering for the same account, which is the id coverage knows it
 * by. The newest wins because its context is the freshest statement of what was asked.
 */
function newestCompletedAskIds(rows: BackBooksRow[]): Set<string> {
    const newestByAccount = new Map<string, Task>();
    rows.forEach(({ task, account }) => {
        if (task.status !== TaskStatuses.COMPLETED || account.bank_account_id === null) {
            return;
        }
        const incumbent = newestByAccount.get(account.bank_account_id);
        if (incumbent === undefined || isNewerAsk(task, incumbent)) {
            newestByAccount.set(account.bank_account_id, task);
        }
    });
    return new Set([...newestByAccount.values()].map((task) => task.id));
}

/**
 * One row per child task, numbered from live coverage.
 *
 * Rows come from tasks because the unit is one task per account, and the group card's `tasks_count`
 * has to agree with what the panel lists. Every number comes from coverage because the context is a
 * snapshot: a task is only ever created after coverage already showed the account owing months
 * (skipped during accounting setup, or added by an operator), so tasks are a subset of coverage's
 * gap accounts and never a superset.
 *
 * THE JOIN, canonically — every other mention of it in this feature refers back here.
 *
 * Joined on `bank_account_id` alone, deliberately with no second key to fall back on. Identity has
 * to be whatever *closes* the task, and completion fires when live coverage says the account owes
 * nothing — coverage identifies an account by that id and nothing else. Two accounts at one bank
 * sharing a mask and type are one key under any name-derived tuple, so a fallback would complete
 * the pair's card as soon as either was satisfied, and `reopen_policy` is `deny`: the other's
 * months could never be asked for again. The task context's schema rules the tuple out for the
 * same reason.
 *
 * So three tasks are dropped rather than guessed at: one whose account coverage no longer lists (the
 * member disconnected it, and nothing is owed on an account that no longer exists), one whose
 * context carries no id at all, which the producer says it never writes, and a completed ask
 * superseded by a live one — see `accountIdsWithOpenAsk`, and `newestCompletedAskIds` for the
 * duplicate that outlives it.
 */
export default function toRows(tasks: Task[], accounts: AccountStatementCoverage[]): BackBooksRow[] {
    // The account id and nothing else. Only accounts Tight has assigned an id to can appear, which
    // is all of them: coverage drops an account whose v6 id will not parse rather than nulling it.
    const byId = new Map(
        accounts
            .filter((account) => account.bank_account_id !== null)
            .map((account) => [account.bank_account_id as string, account])
    );
    const openAskAccountIds = accountIdsWithOpenAsk(tasks);

    const rows = tasks.flatMap((task) => {
        if (!isBulkBankStatementUploadContext(task.context)) {
            return [];
        }
        const id = v6AccountId(task.context.bank_account_id);
        if (id === null) {
            return [];
        }
        const account = byId.get(id);
        if (!account) {
            return [];
        }
        if (task.status === TaskStatuses.COMPLETED && openAskAccountIds.has(id)) {
            return [];
        }
        const askedPeriods = askedPeriodsFor(task.context, account);
        const missing = periodsNotHeld(askedPeriods, account.held_periods);
        return [{ task, account, askedPeriods, missing, state: rowState(missing, account) }];
    });

    // Applied to the rows rather than the tasks: duplicates are per coverage account, and only a
    // row has been joined to one. A task whose account coverage dropped is already gone by here.
    const rendered = newestCompletedAskIds(rows);
    return rows.filter((row) => row.task.status !== TaskStatuses.COMPLETED || rendered.has(row.task.id));
}

/**
 * Whether an uploaded document's status belongs to this coverage account.
 *
 * Match on the v6 id alone. The two account fields on a status are populated together — both come
 * from the `statement_account` record written when the document was filed — so a null id means the
 * document was never filed against anything, and a mask fallback would have nothing to match on.
 *
 * The consequence is worth stating: a coverage account Tight has assigned no id to can never own a
 * filed document, because `assign-account` keys on that id. Such an account's files are visible only
 * through the session record in the panel, and only until the page reloads. See `canFileUploads`.
 */
export function statusBelongsToAccount(
    status: Pick<UploadedStatementStatus, 'bank_account_id'>,
    account: AccountStatementCoverage
): boolean {
    return account.bank_account_id !== null && status.bank_account_id === account.bank_account_id;
}

/**
 * Whether an upload against this account can actually be filed.
 *
 * `assign-account` keys on the v6 id, so an account without one accepts the upload and then has
 * nowhere to record it. Callers use this to avoid offering an action that silently does nothing.
 */
export function canFileUploads(account: AccountStatementCoverage): boolean {
    return account.bank_account_id !== null;
}

/**
 * Whether an uploaded document belongs to *this* ask, rather than to the account's whole history.
 *
 * Membership on the account is not enough. A member who has been filing statements for years has
 * dozens credited against the same account, and listing all of them turns the modal into an archive
 * — most rows reading "covers a month we don't need", each with a live delete button.
 *
 * Two rules, because the two cases carry different evidence:
 * - A credited document is relevant only if the month it credited is one we are still asking for.
 *   That is what drops the old history, and it keeps a month already covered by an older upload
 *   showing as matched rather than falsely missing.
 * - Anything else — failed, unreadable, wrong account, still processing — has no month to judge, so
 *   it is relevant if it arrived after the task did. That reads as an attempt at this ask, and
 *   unlike a session record it survives a reload.
 */
export function isRelevantToAsk(
    status: UploadedStatementStatus | undefined,
    documentCreatedAt: string | undefined,
    row: Pick<BackBooksRow, 'askedPeriods' | 'task'>
): boolean {
    // Read through `toStatementVerdict` rather than off the wire value directly. It is the one
    // place that maps a status to a meaning, and its exhaustive switch turns a renamed or split
    // `tracked` into a compile error. Testing `status === 'tracked'` here would instead fail
    // silently, and in the worse direction: every filed statement would become irrelevant to the
    // ask and vanish from the modal, which is precisely the archive-vs-hidden trade-off above.
    const verdict = toStatementVerdict(status);
    if (verdict.kind === 'matched') {
        // This task's ask, not the account's whole window. The window reaches back to the books
        // start, so it admits every statement credited before the task existed — and each arrived
        // in the couldn't-match list with a live month menu and delete, both acting on a month no
        // slot here lists: the menu re-pointed the credit and the delete released it, silently
        // either way, and the modal's own months could not put it back. A credit this task never
        // asked about is not this modal's to move; keeping the file out keeps the controls out.
        return verdict.coveredPeriods.some((period) => includesPeriod(row.askedPeriods, period));
    }
    // Fails open on a missing timestamp. Hiding a file the member uploaded is the worse error of the
    // two — they cannot delete or re-file what they cannot see — so an unanswerable case shows it.
    if (!documentCreatedAt) {
        return true;
    }
    // Compared as instants, not as strings. Hardening rather than an observed failure: the two
    // orders only disagree when the timestamps match down to the second, since `isoformat()` drops
    // the fractional part only at exactly zero microseconds, and 'Z' then sorts after '.'. Parsing
    // also survives a serializer rendering a non-UTC offset, where string order means nothing.
    const uploadedAt = Date.parse(documentCreatedAt);
    const askedAt = Date.parse(row.task.created_at);
    if (Number.isNaN(uploadedAt) || Number.isNaN(askedAt)) {
        return true;
    }
    return uploadedAt >= askedAt;
}
